/* Shared-device persistence: server records are authoritative; old v1 data is never erased. */
const SandPlatform = (() => {
  const collections = ['students', 'showcase', 'knowledge', 'agentOverrides', 'weaponOverrides', 'exhibition'];
  const clone = v => JSON.parse(JSON.stringify(v));
  const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  function rebase(before, after, server) {
    if (same(before, after)) return server;
    if (before && after && server && !Array.isArray(before) && !Array.isArray(after) && typeof before === 'object' && typeof after === 'object') {
      const result = { ...server };
      for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
        if (!(key in after)) delete result[key];
        else result[key] = rebase(before[key], after[key], server[key]);
      }
      return result;
    }
    return after;
  }
  const entities = (state, name) => name === 'showcase' ? Object.fromEntries((state[name] || []).map(x => [x.id, x])) : state[name] || {};
  let defaults, current, baseline, raw = {}, renderer, timer, poller, busy, lastError = '', status = '正在连接数据服务…', ready = false;
  let auth = { role: 'visitor' }, setupRequired = false, connected = false, generation = 0, revision = -1, refreshing = false;
  const listeners = new Set();
  const notify = () => listeners.forEach(fn => fn());
  const diff = (before, after) => collections.flatMap(collection => {
    const a = entities(before, collection), b = entities(after, collection);
    return [...new Set([...Object.keys(a), ...Object.keys(b)])].filter(key => !same(a[key], b[key])).map(key => ({ collection, key, value: b[key] ?? null }));
  });
  function patch(state, updates) {
    const next = { ...state };
    updates.forEach(({ collection, key, value }) => {
      const values = { ...entities(next, collection) };
      if (value == null) delete values[key]; else values[key] = value;
      next[collection] = collection === 'showcase' ? Object.values(values) : values;
    });
    return next;
  }
  function effective(data) {
    return { ...defaults, ...data, students: data.students || {}, showcase: data.showcase || [],
      knowledge: { ...defaults.knowledge, ...(data.knowledge || {}) }, agentOverrides: data.agentOverrides || {}, weaponOverrides: data.weaponOverrides || {}, exhibition: data.exhibition || {},
      anonChats: current?.anonChats || {}, session: current?.session || null };
  }
  function emit(next) { current = next; renderer?.(next); notify(); }
  function initial() {
    if (current) return current;
    defaults = seedInitialState();
    // Never expose cached real student/teacher records before authentication.
    current = effective({}); baseline = clone(current);
    try { const session = JSON.parse(sessionStorage.getItem('sand-world-visitor::v2') || '{}'); current.anonChats = session.anonChats || {}; } catch {}
    return current;
  }
  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), options.timeout || 20000);
    try {
      const r = await fetch(url, { credentials: typeof isExhibitionKiosk === 'function' && isExhibitionKiosk() ? 'omit' : 'same-origin', ...options, signal: options.signal || controller.signal });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { const error = new Error(data.error || `服务请求失败 (${r.status})`); error.status = r.status; error.payload = data; throw error; }
      return data;
    } catch (e) { if (e.name === 'AbortError') throw new Error('连接超时，请确认本地服务仍在运行。'); throw e; }
    finally { clearTimeout(timeout); }
  }
  function adopt(packet, keepSession = true) {
    revision = packet.revision ?? revision;
    raw = clone(packet.state || {}); auth = packet.auth || { role: 'visitor' }; setupRequired = !!packet.setupRequired;
    connected = true; ready = true; lastError = ''; status = '已保存到本机服务';
    const next = effective(raw);
    if (!keepSession) { generation++; next.session = null; next.anonChats = {}; }
    const ai = next.exhibition?.ai;
    if (ai) { AI_CONFIG.provider = ai.mode === 'mock' ? 'mock' : 'proxy'; AI_CONFIG.fallbackToMockOnError = !!ai.fallbackEnabled; }
    baseline = clone(next); emit(next);
  }
  async function refresh() {
    if (busy || refreshing || diff(baseline, current).length) return;
    const epoch = generation; refreshing = true;
    try {
      const packet = await request('/api/state');
      // A slow poll must never overwrite an edit, a successful write, or a newer login.
      if (epoch !== generation || busy || diff(baseline, current).length || packet.revision < revision) return;
      adopt(packet);
    }
    catch (e) { if (epoch === generation) { connected = false; ready = true; status = '数据服务未连接'; lastError = e.message; notify(); } }
    finally { refreshing = false; }
  }
  function update(updater) {
    const next = typeof updater === 'function' ? updater(current) : updater;
    emit(next);
    try { sessionStorage.setItem('sand-world-visitor::v2', JSON.stringify({ anonChats: next.anonChats })); }
    catch { status = '访客记录仅保留在当前页面'; notify(); }
    if (diff(baseline, next).length) {
      status = '正在保存…'; notify(); clearTimeout(timer); timer = setTimeout(() => flush().catch(() => {}), 450);
    }
  }
  async function flush() {
    clearTimeout(timer);
    if (busy) { await busy; if (diff(baseline, current).length) return flush(); return; }
    if (!diff(baseline, current).length) return;
    if (auth.role === 'visitor') throw new Error('请先登录并连接数据服务；当前内容尚未保存。');
    busy = (async () => {
      while (diff(baseline, current).length) {
        const sent = clone(current);
        const updates = diff(baseline, sent).map(u => ({ ...u, expected: entities(raw, u.collection)[u.key] ?? null }));
        const packet = await request('/api/state', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
        const inFlightChanges = diff(sent, current);
        raw = clone(packet.state || {}); auth = packet.auth || auth;
        revision = packet.revision ?? revision;
        connected = true;
        if (raw.exhibition?.ai) { AI_CONFIG.provider = raw.exhibition.ai.mode === 'mock' ? 'mock' : 'proxy'; AI_CONFIG.fallbackToMockOnError = !!raw.exhibition.ai.fallbackEnabled; }
        baseline = effective(raw);
        emit(patch(baseline, inFlightChanges.map(u => ({ ...u, value: u.value === null ? null : rebase(entities(sent, u.collection)[u.key], u.value, entities(baseline, u.collection)[u.key]) }))));
      }
      lastError = ''; status = '已保存到本机服务'; notify();
    })();
    try { await busy; }
    catch (e) { lastError = e.status === 409 ? '同一记录已在另一台设备修改。请先导出未保存内容，再重新读取；不会自动覆盖。' : e.message; status = '尚未保存，请勿关闭页面'; notify(); throw e; }
    finally { busy = null; }
  }
  async function commit(updater) { update(updater); await flush(); }
  async function login(kind, payload) {
    if (diff(baseline, current).length) await flush();
    generation++;
    const packet = await request('/api/auth/' + kind, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    adopt(packet, false); return packet;
  }
  async function logout() {
    await flush();
    generation++;
    const packet = await request('/api/auth/logout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    sessionStorage.removeItem('sand-world-visitor::v2'); adopt(packet, false);
  }
  function mount(render) {
    renderer = render; initial(); refresh();
    poller = setInterval(refresh, 5000);
    const guard = e => { if (diff(baseline, current).length) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', guard);
    return () => { clearInterval(poller); window.removeEventListener('beforeunload', guard); renderer = null; };
  }
  function download(name, value) {
    const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return { initial, mount, update, commit, flush, login, logout, refresh, request, download,
    subscribe: fn => { listeners.add(fn); return () => listeners.delete(fn); },
    get state() { return current; }, get auth() { return auth; }, get generation() { return generation; }, get ready() { return ready; }, get connected() { return connected; },
    get setupRequired() { return setupRequired; }, get status() { return status; }, get error() { return lastError; },
    get pending() { return !!current && diff(baseline, current).length > 0; },
    hasStoredEntity: (collection, key) => Object.prototype.hasOwnProperty.call(entities(raw, collection), key),
    async reloadDiscardingDraft() { download('一沙一世界-未保存内容备份.json', current); baseline = clone(current); await refresh(); },
    async upload(file) {
      if (auth.role === 'visitor') throw new Error('上传作品请先登录学生或教师账号。');
      return request('/api/media', { method: 'POST', headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name) }, body: file, timeout: 90000 });
    },
  };
})();
window.SandPlatform = SandPlatform;

function usePlatformStatus() {
  const [, rerender] = useState(0);
  useEffect(() => SandPlatform.subscribe(() => rerender(x => x + 1)), []);
}
function PlatformStatus() {
  usePlatformStatus();
  return <div className={`platform-status ${SandPlatform.error ? 'has-error' : ''}`} role="status">
    <span className="status-dot"/>{SandPlatform.auth.role === 'visitor' && SandPlatform.connected ? '访客体验 · 真实作品由教师审核发布' : SandPlatform.status}
    {SandPlatform.error && <Fragment><span>{SandPlatform.error}</span><button onClick={() => SandPlatform.flush().catch(() => {})}>重试保存</button><button onClick={() => { if (confirm('先下载当前未保存内容的备份，再读取服务器版本？')) SandPlatform.reloadDiscardingDraft(); }}>备份并重新读取</button></Fragment>}
  </div>;
}

function PlatformStudentSignIn({ onSignedIn, onClose }) {
  const [code, setCode] = useState('WU1'), [name, setName] = useState(''), [pin, setPin] = useState(''), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  async function go(e) {
    e.preventDefault(); if (busy) return;
    if (!ROLES_BY_CODE[code.trim().toUpperCase()]) return setError('请选择正确的班级码。');
    setBusy(true); setError('');
    try { const packet = await SandPlatform.login('student', { name: name.trim(), classCode: code.trim().toUpperCase(), pin }); onSignedIn(packet.auth.studentId); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  return <form onSubmit={go} className="platform-login"><div className="flex justify-between gap-3"><h2>继续我的学习</h2><button type="button" className="sand-button sand-button-ghost" onClick={onClose}>关闭</button></div><p>第一次使用会创建记录。再次输入相同班级码、姓名与个人 PIN，就能在连接同一服务的设备上继续。</p><label>班级码<select value={code} onChange={e => setCode(e.target.value)}>{ROLES.map(r => <option key={r.id} value={r.classCode}>{r.classCode} · {r.teamName}</option>)}</select></label><label>姓名或学习昵称<input autoComplete="username" value={name} maxLength={30} onChange={e => setName(e.target.value)} required/></label><label>个人 PIN（4～12 位数字）<input type="password" inputMode="numeric" autoComplete="current-password" value={pin} onChange={e => setPin(e.target.value)} pattern="[0-9]{4,12}" required/></label><p className="text-xs">不要使用生日等容易猜到的 PIN，也不要输入身份证或联系电话。忘记 PIN 请联系老师重置。</p>{error && <p role="alert" className="platform-error">{error}</p>}<button className="sand-button" disabled={busy}>{busy ? '正在连接…' : '登录 / 首次创建学习记录'}</button></form>;
}

function PlatformTeacherSignIn({ onSignedIn, onClose }) {
  usePlatformStatus();
  const [password, setPassword] = useState(''), [confirmation, setConfirmation] = useState(''), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const setup = SandPlatform.setupRequired;
  async function go(e) {
    e.preventDefault(); if (busy) return;
    if (setup && password !== confirmation) return setError('两次密码不一致。');
    setBusy(true); setError('');
    try { await SandPlatform.login(setup ? 'setup' : 'teacher', { password }); onSignedIn(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  return <form onSubmit={go} className="platform-login"><div className="flex justify-between gap-3"><h2>{setup ? '首次设置教师密码' : '教师工作台'}</h2><button type="button" className="sand-button sand-button-ghost" onClick={onClose}>关闭</button></div><p>{setup ? '只允许在运行服务的本机完成首次设置。请设置至少 10 个字符的密码，并妥善保管。' : '登录后可审核学习记录、管理真实作品和授权、调整导师、导出数据备份。'}</p><label>教师密码<input type="password" minLength={10} value={password} autoComplete={setup ? 'new-password' : 'current-password'} onChange={e => setPassword(e.target.value)} required/></label>{setup && <label>再次输入密码<input type="password" value={confirmation} autoComplete="new-password" onChange={e => setConfirmation(e.target.value)} required/></label>}{error && <p className="platform-error" role="alert">{error}</p>}<button className="sand-button" disabled={busy}>{busy ? '正在验证…' : setup ? '设置密码并进入工作台' : '安全登录工作台'}</button></form>;
}

function PlatformDataManager({ state, toast, onBack }) {
  const [busy, setBusy] = useState(false), [info, setInfo] = useState(null), [selectedStudent, setSelectedStudent] = useState(''), [pin, setPin] = useState('');
  useEffect(() => { SandPlatform.request('/api/storage-status').then(setInfo).catch(() => {}); }, []);
  async function importLegacy() {
    const old = localStorage.getItem(STORE_KEY);
    if (!old) return toast('这个浏览器没有旧版学习记录。', 'error');
    if (!confirm('保留旧版原始记录不变，并将尚未导入的记录复制到服务端？所有旧作品先设为未发布，需重新核对授权。')) return;
    setBusy(true);
    try {
      const oldState = JSON.parse(old); SandPlatform.download('一沙一世界-旧版原始记录备份.json', oldState);
      async function migrate(value) {
        if (!value || typeof value !== 'object') return value;
        if (Array.isArray(value)) return Promise.all(value.map(migrate));
        const next = { ...value };
        if (typeof next.data === 'string' && next.data.startsWith('data:')) {
          const blob = await (await fetch(next.data)).blob();
          const ext = { 'image/png': '.png', 'image/jpeg': '.jpg', 'image/webp': '.webp', 'video/mp4': '.mp4', 'audio/mpeg': '.mp3', 'application/pdf': '.pdf' }[blob.type] || '.txt';
          const file = new File([blob], /\.[a-z0-9]+$/i.test(next.label || '') ? next.label : (next.label || '旧版附件') + ext, { type: blob.type });
          return { ...next, ...await SandPlatform.upload(file) };
        }
        for (const key of Object.keys(next)) if (typeof next[key] === 'object') next[key] = await migrate(next[key]);
        return next;
      }
      const imported = await migrate(oldState);
      await SandPlatform.commit(s => ({ ...s,
        students: { ...Object.fromEntries(Object.entries(imported.students || {}).map(([id, student]) => [id, { ...student, isDemo: student.isDemo || DEMO_STUDENTS.some(x => x.id === id) }])), ...s.students },
        showcase: [...s.showcase, ...(imported.showcase || []).filter(x => !s.showcase.some(y => y.id === x.id)).map(x => ({ ...x, published: false, consentConfirmed: false, isDemo: /^sh-\d+$/.test(x.id), displayName: '学习小组' }))],
        knowledge: Object.fromEntries([...new Set([...Object.keys(imported.knowledge || {}), ...Object.keys(s.knowledge)])].map(key => {
          const old = imported.knowledge?.[key] || [], existing = s.knowledge[key] || [];
          return [key, [...new Map((SandPlatform.hasStoredEntity('knowledge', key) ? [...old, ...existing] : [...existing, ...old]).map(item => [item.id, item])).values()]];
        })),
        agentOverrides: { ...(imported.agentOverrides || {}), ...s.agentOverrides }, weaponOverrides: { ...(imported.weaponOverrides || {}), ...s.weaponOverrides },
      }));
      toast('旧版记录已复制保存；学生需由老师设置 PIN 后继续。', 'success');
    } catch (e) { toast('导入未完成：' + e.message + '。旧版原始数据仍然保留。', 'error'); }
    finally { setBusy(false); }
  }
  async function resetPin(e) {
    e.preventDefault(); setBusy(true);
    try { await SandPlatform.request('/api/auth/student-pin', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ studentId: selectedStudent, pin }) }); setPin(''); toast('学生 PIN 已更新，请私下告知该学生。', 'success'); } catch (e) { toast(e.message, 'error'); } finally { setBusy(false); }
  }
  return <div className="paper-card p-6 platform-data"><button className="sand-button sand-button-ghost" onClick={onBack}>返回工作台</button><h2>数据保存与展前准备</h2><p>文字记录和附件保存在运行服务的电脑中。其他设备连接同一服务后，按登录身份同步；教师能查看学生提交，访客只能查看已授权发布的作品。</p><div className="flex flex-wrap gap-3"><a className="sand-button" href="/api/backup" download>下载业务数据备份</a><button className="sand-button sand-button-ghost" disabled={busy} onClick={importLegacy}>导入这个浏览器的旧版记录</button><a className="sand-button sand-button-ghost" href="/?exhibit=1" target="_blank" rel="noopener">打开游客展览模式</a></div><p className="text-xs">JSON 备份包含业务记录与附件索引；完整恢复还需要备份服务器 data 目录中的媒体文件。旧版浏览器数据不会被删除。新学生作品默认不公开。</p>{info && <p>数据服务检查：{info.ok === false ? '请检查服务' : '可连接'}。请在布展前保留一份完整文件夹备份。</p>}<h3>为已有学生设置 / 重置 PIN</h3><form className="platform-login" onSubmit={resetPin}><label>选择学生<select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)} required><option value="">请选择</option>{Object.values(state.students).map(s => <option key={s.id} value={s.id}>{s.name} · {s.class}</option>)}</select></label><label>新的个人 PIN<input type="password" inputMode="numeric" pattern="[0-9]{4,12}" value={pin} onChange={e => setPin(e.target.value)} required/></label><button className="sand-button" disabled={busy}>保存新 PIN</button></form><h3>真实素材准备清单</h3><p>请准备 3～5 个已获授权的学生案例：最初问题、AI 引导片段、学生修改过程、最终成果；每件至少一份照片、短视频、录音或文档。公开署名使用昵称或小组名称。教师审核后，再到成果展编辑页确认授权并发布。</p></div>;
}

function PlatformAISettings({ state, onBack, toast }) {
  const [health, setHealth] = useState(null), [busy, setBusy] = useState(false), [result, setResult] = useState(null);
  const config = state.exhibition?.ai || { mode: 'proxy', fallbackEnabled: false };
  useEffect(() => { checkProxyHealth().then(setHealth); }, []);
  async function save(patch) {
    try { await SandPlatform.commit(s => ({ ...s, exhibition: { ...s.exhibition, ai: { ...config, ...patch } } })); toast('AI 运行方式已保存，同一服务下的设备会自动更新。', 'success'); } catch (e) { toast(e.message, 'error'); }
  }
  async function test() {
    setBusy(true); setResult(null); const start = performance.now();
    try { const text = await callProxy({ systemPrompt: '你是一位五年级科学导师。简洁友好，用一个问题引导学生观察。最多60字。', messages: [{ role: 'user', content: '沙子遇到水为什么颜色变深？' }], model: '' }); setResult({ ok: true, seconds: ((performance.now() - start) / 1000).toFixed(1), text }); }
    catch (e) { setResult({ ok: false, text: e.message }); } finally { setBusy(false); }
  }
  return <div className="paper-card p-6 platform-data"><button className="sand-button sand-button-ghost" onClick={onBack}>返回工作台</button><h2>AI 运行与现场检查</h2><p>默认通过本机服务调用真实 AI；密钥只放在服务端，不需要在网页输入。检查服务“已配置”不等于真实调用成功，请在布展网络下测试。</p><p>服务连接：{health?.ok ? '正常' : '待确认'} · 模型：{health?.model || '读取中'} · 密钥：{health?.hasKey ? '已配置（不显示密钥）' : '尚未配置'}</p><div className="platform-login"><label>运行方式<select value={config.mode} onChange={e => save({ mode: e.target.value })}><option value="proxy">真实 AI（需要联网）</option><option value="mock">本地教学模拟（必须标明示例）</option></select></label><label><span><input style={{width:'auto'}} type="checkbox" checked={config.fallbackEnabled} onChange={e => save({ fallbackEnabled: e.target.checked })}/> 联网失败时提供明确标注的本地模拟参考</span></label></div><p>模拟不是大模型回答；关闭自动模拟时，失败会提示重试。图片生成使用服务端配置的通义万相，与制作页面插图使用的 Codex Image2 是独立功能。</p><button className="sand-button" disabled={busy} onClick={test}>{busy ? '正在测试真实回复…' : '真实对话测试一次（使用现有接口额度）'}</button>{result && <div className="paper-card p-4 mt-4" role="status"><strong>{result.ok ? `真实调用成功 · ${result.seconds} 秒` : '真实调用失败'}</strong><p>{result.text}</p></div>}<h3>展前验收要点</h3><p>逐个导师提问，切换八种学习方法；测试图片生成；在学生端上传并提交，在教师端审核并发布，最后用未登录浏览器查看作品。规则参考评分和教师发起的 AI 文字评阅是两种不同能力，最终评分由老师确认。</p></div>;
}
